import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";

import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";

import { Label } from "components/ui/label";

import { fetchAxiosToken } from "helpers/fetchAxiosToken";
import { toast } from "components/ui/use-toast";

import { ReloadIcon } from "@radix-ui/react-icons";

import dayjs from "dayjs";
import { Button } from "components/ui/button";
import { useAppSelector } from "hooks/hooks";

interface Props {
    setEdit: (visible: ScheduleBlocked | null) => void;
    edit: ScheduleBlocked;
    getEvents: () => void;
}

export interface ScheduleBlocked {
    id: string;
    start: string;
    end: string;
}

export interface Intervals {
    closing_time: string;
    opening_time: string;
}

export const UpdateScheduleBlocked = ({ setEdit, edit, getEvents }: Props) => {
    const startDate = new Date(edit.start);
    const endDate = new Date(edit.end);

    const { id } = useAppSelector((state) => state.usuario_logeado);

    const [intervals, setIntervals] = useState<Intervals[]>([]);
    const [startTime, setStartTime] = useState<string>(
        startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
    const [endTime, setEndTime] = useState<string>(
        endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );

    const [loading, setLoading] = useState<boolean>(false);
    const [lodingIntervals, setLoadingIntervals] = useState<boolean>(false);
    const [lodingCanceling, setLoadingCanceling] = useState<boolean>(false);

    const handleSubmit = async () => {
        if (edit === null) {
            return;
        }
        if (startTime === "" || endTime === "") {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Seleccione la hora de inicio y fin",
            });
            return;
        }

        if (
            dayjs(
                `${
                    new Date(edit.start).toISOString().split("T")[0]
                } ${startTime}`,
                "YYYY-MM-DD HH:mm"
            ).isAfter(
                dayjs(
                    `${
                        new Date(edit.start).toISOString().split("T")[0]
                    } ${endTime}`,
                    "YYYY-MM-DD HH:mm"
                )
            )
        ) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "La hora de inicio no puede ser mayor a la de fin",
            });
            return;
        }
        setLoading(true);
        try {
            await fetchAxiosToken({
                url: `/horarios_no_disponibles/update/${edit.id}`,
                method: "put",
                body: {
                    hora_inicio: startTime,
                    hora_final: endTime,
                },
            });

            getEvents();
            toast({
                title: "Datos actualizados correctamente",
            });
            setTimeout(() => {
                setLoading(false);
            }, 2000);
            setEdit(null);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error",
                description: <div>{error.response.data?.message}</div>,
            });
            setLoading(false);
        }
    };

    const getOffices = async () => {
        try {
            const dataInterval = await fetchAxiosToken({
                url: `/horarios_asesores/getIntervals/${id}`,
                body: {
                    date: dayjs(edit.start).format("YYYY-MM-DD"),
                },
                method: "post",
            });

            setIntervals(dataInterval.intervals);
            setLoadingIntervals(false);
        } catch (error: any) {
            console.log(error);
            setLoadingIntervals(false);
        }
    };

    const cancelBlocked = async () => {
        if (edit === null) {
            return;
        }
        setLoadingCanceling(true);
        try {
            await fetchAxiosToken({
                url: `/horarios_no_disponibles/delete/${edit.id}`,
                method: "delete",
            });

            getEvents();
            toast({
                title: "Bloqueo cancelado correctamente",
            });
            setTimeout(() => {
                setLoadingCanceling(false);
            }, 2000);
            setEdit(null);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error",
                description: <div>{error.response.data?.message}</div>,
            });
            setLoadingCanceling(false);
        }
    };

    useEffect(() => {
        getOffices();
    }, []);

    return (
        <div className="absolute select-none">
            <Dialog defaultOpen={true} onOpenChange={() => setEdit(null)}>
                <DialogContent className="sm:max-w-[425px] select-none">
                    <DialogHeader>
                        <DialogTitle className="capitalize">
                            {" "}
                            {edit
                                ? ` ${dayjs(edit.start).format(
                                      "dddd"
                                  )} ${new Date(
                                      edit.start
                                  ).toLocaleDateString()}`
                                : ""}
                        </DialogTitle>
                        <DialogDescription>
                            Actualizar horario bloqueado
                        </DialogDescription>
                    </DialogHeader>

                    {lodingIntervals ? (
                        <div className="items-center justify-center w-full py-4">
                            <ReloadIcon className="w-12 h-12 mx-auto animate-spin text-blue-consulta-900" />
                        </div>
                    ) : (
                        <div className="grid gap-4 py-4">
                            <div className="grid items-center grid-cols-4 gap-4">
                                <div className="col-span-4">
                                    <Label
                                        htmlFor="Carrera"
                                        className="text-right">
                                        Hora de inicio
                                    </Label>
                                </div>

                                <div className="col-span-4">
                                    <Select
                                        value={startTime}
                                        onValueChange={(value) =>
                                            setStartTime(value)
                                        }>
                                        <SelectTrigger
                                            translate="no"
                                            className="w-full">
                                            <SelectValue
                                                translate="no"
                                                placeholder="Selecciona la hora de inicio"
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup
                                                translate="no"
                                                className="pt-6 pb-14 xs400:pt-2 xs400:pb-10">
                                                <SelectItem value={startTime}>
                                                    {startTime}
                                                </SelectItem>
                                                {intervals.map(
                                                    (time, index) =>
                                                        time.opening_time !==
                                                            startTime && (
                                                            <SelectItem
                                                                translate="no"
                                                                key={index}
                                                                value={
                                                                    time.opening_time
                                                                }>
                                                                {
                                                                    time.opening_time
                                                                }
                                                            </SelectItem>
                                                        )
                                                )}
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid items-center grid-cols-4 gap-4">
                                <div className="col-span-4">
                                    <Label
                                        htmlFor="Carrera"
                                        className="text-right">
                                        Hora de fin
                                    </Label>
                                </div>

                                <div className="col-span-4">
                                    <Select
                                        value={endTime}
                                        onValueChange={(value) =>
                                            setEndTime(value)
                                        }>
                                        <SelectTrigger
                                            translate="no"
                                            className="w-full">
                                            <SelectValue
                                                translate="no"
                                                placeholder="Selecciona la hora de fin"
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup
                                                translate="no"
                                                className="py-6 xs400:py-2">
                                                <SelectItem
                                                    translate="no"
                                                    value={endTime}>
                                                    {endTime}
                                                </SelectItem>
                                                {intervals.map(
                                                    (time, index) =>
                                                        time.closing_time !==
                                                            endTime && (
                                                            <SelectItem
                                                                translate="no"
                                                                key={index}
                                                                value={
                                                                    time.closing_time
                                                                }>
                                                                {
                                                                    time.closing_time
                                                                }
                                                            </SelectItem>
                                                        )
                                                )}
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            onClick={() => cancelBlocked()}
                            disabled={lodingCanceling}
                            type="button"
                            className="bg-red-400 select-none hover:bg-red-300">
                            {lodingCanceling ? (
                                <>
                                    <ReloadIcon className="w-4 h-4 mr-2 animate-spin" />
                                    Cargando
                                </>
                            ) : (
                                "Cancelar bloqueo"
                            )}
                        </Button>

                        <Button
                            className="bg-[#029BFC]"
                            disabled={loading}
                            onClick={() => handleSubmit()}
                            type="submit">
                            {loading ? (
                                <>
                                    <ReloadIcon className="w-4 h-4 mr-2 animate-spin " />
                                    Cargando
                                </>
                            ) : (
                                "Actualizar"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
